import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { ReconcileRow, allocText } from "@/components/containers/ReconcileRow";
import { ReconcileReview } from "@/components/containers/ReconcileReview";

const key = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `k${Date.now()}${Math.random().toString(16).slice(2)}`);
const fromLine = (l) => ({ key: l.line_id, line_id: l.line_id, sku: l.sku, expected: String(l.expected_boxes), rec: l.received ? { ...l.received } : null,
  pallets: allocText(l.pallet_alloc), palletsEdited: false, reason: "", note: "", remove: false, merge_into: null, orig: l });
const blank = (patch) => ({ key: key(), line_id: null, sku: "", expected: "", rec: null, pallets: "", palletsEdited: false, reason: "", note: "", remove: false, merge_into: null, ...patch });

const parseAlloc = (text, qty) => {
  const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 1 && !parts[0].includes(":")) return [{ pallet: Number(parts[0]), boxes: qty }];
  return parts.map((p) => { const [a, b] = p.split(":"); return { pallet: Number(a), boxes: Number(b) }; });
};

const changed = (r) => !r.orig || r.remove || r.merge_into || r.sku !== r.orig.sku || Number(r.expected) !== r.orig.expected_boxes || r.palletsEdited
  || (r.rec && JSON.stringify(Object.values(r.rec).map(Number)) !== JSON.stringify(Object.values(r.orig.received || {}).map(Number)));

const toLine = (r, received) => {
  const rec = received && r.rec ? { boxes_received: Number(r.rec.boxes_received || 0), damaged: Number(r.rec.damaged || 0), unsellable: Number(r.rec.unsellable || 0), quarantined: Number(r.rec.quarantined || 0) } : null;
  const qty = rec ? rec.boxes_received : Number(r.expected || 0);
  return { line_id: r.line_id, sku: r.sku, expected_boxes: Number(r.expected || 0), received: rec, remove: r.remove, merge_into: r.merge_into,
    pallet_alloc: r.palletsEdited ? (r.pallets.trim() ? parseAlloc(r.pallets, qty) : []) : null,
    origin: r.line_id ? null : r.origin, excluded_ref: r.excluded_ref || null, split_from: r.split_from || null, reason_code: r.reason || null, note: r.note };
};

// ID-078 Reconcile Inventory: edit → server review (no writes) → one atomic confirmation
export const ReconcileInventoryDialog = ({ containerId, open, onOpenChange, onDone }) => {
  const [ctx, setCtx] = useState(null);
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);
  const [idem, setIdem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const load = async () => {
    const { data } = await api.get(`/containers/${containerId}/reconcile`);
    setCtx(data); setRows(data.lines.filter((l) => l.status === "ACTIVE").map(fromLine)); setReview(null); setDone(null); setIdem(null);
  };
  useEffect(() => { if (open) load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const received = ctx?.status === "RECEIVED";
  const setRow = (k, v) => { setReview(null); setRows((rs) => (v ? rs.map((r) => (r.key === k ? v : r)) : rs.filter((r) => r.key !== k))); };
  const addRow = (patch) => { setReview(null); setRows((rs) => [...rs, blank({ rec: received ? { boxes_received: "", damaged: 0, unsellable: 0, quarantined: 0 } : null, ...patch })]); };
  const split = (r) => addRow({ origin: "SPLIT", split_from: r.line_id, splitSku: r.orig.sku, reason: "SUBSTITUTION", pallets: r.pallets.includes(",") ? "" : r.pallets.split(":")[0], palletsEdited: false });
  const identify = (e) => {
    const pending = rows.filter((r) => !r.line_id && r.origin === "EXCLUDED_ROW" && r.excluded_ref === e.ref).reduce((n, r) => n + Number(r.expected || 0), 0);
    addRow({ origin: "EXCLUDED_ROW", excluded_ref: e.ref, ex: e, expected: String(Math.max(0, e.remaining_boxes - pending)), reason: "EXCLUDED_ROW_IDENTIFIED" });
  };
  const merge = (r) => {
    const t = rows.find((x) => x.key !== r.key && x.line_id && x.sku === r.sku && !x.remove && !x.merge_into);
    setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, merge_into: t.line_id, reason: x.reason || "DUPLICATE_LINE" }
      : x.key === t.key ? { ...x, expected: String(Number(x.expected) + Number(r.expected)), reason: x.reason || "DUPLICATE_LINE" } : x)));
    setReview(null);
  };
  const payload = (confirm, k) => ({ lines: rows.filter(changed).map((r) => toLine(r, received)), base_version: ctx.version, idempotency_key: k, confirm });
  const doReview = async () => {
    if (!rows.some(changed)) { toast.info("No changes to review"); return; }
    setBusy(true);
    try { const k = key(); const { data } = await api.post(`/containers/${containerId}/reconcile`, payload(false, k)); setReview(data); setIdem(k); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doConfirm = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/containers/${containerId}/reconcile`, payload(true, idem)); setDone(data); toast.success("Reconciliation applied"); onDone?.(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.errors?.join(" · ") || formatApiErrorDetail(d?.message || d)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-border bg-white sm:max-w-6xl" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Reconcile inventory{ctx ? ` · ${ctx.container_number}` : ""}</DialogTitle>
          <DialogDescription className="text-xs text-warmgrey">
            {ctx && (ctx.stage === "MOROCCO" ? "In Morocco — corrections change the expected shipment and Morocco stock; no Florida stock is created."
              : ctx.stage === "INCOMING" ? "In transit — corrections change incoming quantities only; Florida stock is created when the container is received."
              : "Received — enter what was physically counted; differences post to the inventory ledger.")} The supplier's original declaration is never changed.
          </DialogDescription>
        </DialogHeader>
        {!ctx ? <Loader2 className="h-5 w-5 animate-spin text-terracotta" /> : done ? <Done done={done} onClose={() => onOpenChange(false)} onAgain={load} /> : (
          <Body {...{ ctx, rows, received, setRow, addRow, split, merge, identify, review, busy, doReview, doConfirm, onCancel: () => onOpenChange(false), back: () => setReview(null) }} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const Body = ({ ctx, rows, received, setRow, addRow, split, merge, identify, review, busy, doReview, doConfirm, onCancel, back }) => (
  <div className="min-w-0 space-y-4">
    {review ? <ReconcileReview review={review} /> : (
      <>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]" data-testid="reconcile-table">
            <thead><tr className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="py-2">SKU</th><th>Supplier declared</th><th>Corrected expected</th>{received && <th>Physically received · damaged</th>}<th>Difference</th><th>Pallets</th><th>Reason</th><th />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <ReconcileRow key={r.key} r={r} received={received} reasons={ctx.reasons} onChange={(v) => setRow(r.key, v)} onSplit={() => split(r)} onMergeStart={() => merge(r)}
                  canMerge={rows.some((x) => x.key !== r.key && x.line_id && x.sku === r.sku && !x.remove && !x.merge_into)} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => addRow({ origin: "ADDED", reason: "SUPPLIER_CORRECTION" })} data-testid="reconcile-add-sku"><Plus className="h-3.5 w-3.5" /> Add SKU</Button>
          <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => addRow({ origin: "OVERAGE", reason: "OVERAGE" })} data-testid="reconcile-add-overage"><Plus className="h-3.5 w-3.5" /> Add overage</Button>
        </div>
        {ctx.excluded_rows.length > 0 && (
          <div className="rounded-md border border-border bg-alabaster p-3" data-testid="reconcile-excluded">
            <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Excluded supplier rows</p>
            <ul className="mt-2 space-y-1 text-xs text-charcoal">
              {ctx.excluded_rows.map((e) => (
                <li key={e.ref} className="flex flex-wrap items-center gap-2" data-testid={`reconcile-excluded-${e.row}`}>
                  Row {e.row} · <span className="font-mono">{e.supplier_sku}</span> · {e.description} · pallet {e.pallet ?? "—"} · {e.reconciled_boxes}/{e.boxes} identified
                  <span className={`badge-status ${e.status === "RECONCILED" ? "badge-available" : e.status === "PARTIAL" ? "badge-neutral" : "badge-sold"}`}>{e.status === "EXCLUDED" ? "Excluded" : e.status === "PARTIAL" ? "Partly identified" : "Reconciled"}</span>
                  {e.remaining_boxes > 0 && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => identify(e)} data-testid={`reconcile-identify-${e.row}`}>Identify products</Button>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    )}
    <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
      <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="reconcile-cancel">Cancel</Button>
      {review && <Button variant="outline" onClick={back} disabled={busy} data-testid="reconcile-back">Back to edit</Button>}
      {review ? (
        <Button className="bg-terracotta text-white hover:bg-terracotta-hover" disabled={busy || review.blocked} onClick={doConfirm} data-testid="reconcile-confirm">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Confirm reconciliation</Button>
      ) : (
        <Button className="bg-charcoal text-white" disabled={busy} onClick={doReview} data-testid="reconcile-review">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Review changes</Button>
      )}
    </div>
  </div>
);

const Done = ({ done, onClose, onAgain }) => (
  <div className="min-w-0 space-y-4" data-testid="reconcile-done">
    <p className="text-sm text-charcoal">Reconciliation #{done.version} applied{done.replayed ? " (already applied — not repeated)" : ""}. Container: {done.review.totals.after.expected_boxes} expected boxes
      {done.review.totals.after.received_boxes != null ? ` · ${done.review.totals.after.received_boxes} received` : ""} · {done.review.totals.after.sqft.toLocaleString()} sq ft.</p>
    <ReconcileReview review={{ ...done.review, errors: [] }} />
    <div className="flex justify-end gap-2"><Button variant="outline" onClick={onAgain} data-testid="reconcile-again">Reconcile more</Button><Button onClick={onClose} data-testid="reconcile-close">Close</Button></div>
  </div>
);
